<template>
	<NuxtLayout>
		<VitePwaManifest />
		<NuxtLoadingIndicator />
		<NuxtPage v-if="!isLoading" />
	</NuxtLayout>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { buildPageTitle } from "~/utils/seo/titleTemplate";
import { buildHreflangLinks } from "~/utils/locales/buildHreflangLinks";

const isLoading = ref(false);
const i18nHead = useLocaleHead({
	addDirAttribute: true,
	addSeoAttributes: true
});

const pageStore = usePageStore();
const { suppressI18nHead } = storeToRefs(pageStore);

const { locales, defaultLocale } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const siteUrl = useSiteBaseUrl();
const hreflangByCode = useRuntimeConfig().public.hreflangByCode as Record<
	string,
	string
>;

// i18n keys its alternates on `language` (= the Norce iso), so two markets on
// one culture (`us` and `global` on en-US) collapse into one link. Its
// alternates are replaced with ones keyed on the resolved hreflang; the rest
// of its head (canonical, og:locale, html lang) stays. When
// useLocalizedCanonical owns canonical + hreflang (product/category), drop
// both here.
const filteredHead = computed(() => {
	if (!i18nHead.value) return i18nHead.value;

	const i18nLinks = (i18nHead.value.link ?? []).filter((link) => {
		if (link.rel === "alternate" && link.hreflang) return false;
		return !(suppressI18nHead?.value && link.rel === "canonical");
	});
	const alternates = suppressI18nHead?.value
		? []
		: buildHreflangLinks({
				locales: locales.value,
				hreflangByCode,
				defaultLocale,
				hrefFor: (code) => {
					const path = switchLocalePath(
						code as Parameters<typeof switchLocalePath>[0]
					);
					return path ? `${siteUrl}${path}` : undefined;
				}
			});

	return {
		...i18nHead.value,
		link: [...i18nLinks, ...alternates]
	};
});

useHead(filteredHead);

const siteConfig = useSiteConfig();
useHead({
	titleTemplate: (title) => buildPageTitle(title, siteConfig.name)
});

// Purge stale <link hid> nodes — unhead v2 only cleans data-hid variants.
if (import.meta.client) {
	const router = useRouter();
	router.afterEach(() => {
		document.querySelectorAll("link[hid]").forEach((el) => el.remove());
	});
}
</script>
